import { useEffect, useRef, useState } from 'react';

/**
 * Campo de marca em vídeo da tela de entrada.
 *
 * São três peças geradas a partir do conceito da marca — a árvore que cresce
 * de uma semente e se espalha de forma exponencial. Cada uma é um loop de 8s
 * com o mesmo quadro no início e no fim, então a emenda fecha sem salto.
 *
 * Quem escolhe é a URL: /login usa a padrão, /login/2 e /login/3 as outras.
 * Serve para comparar as três no ambiente real antes de fechar uma.
 */

export const VARIANTES = {
  1: {
    id: '06a',
    nomeKey: 'videoField.variants.mirroredRoot.name',
    descricaoKey: 'videoField.variants.mirroredRoot.description',
  },
  2: {
    id: '05c',
    nomeKey: 'videoField.variants.particleCanopy.name',
    descricaoKey: 'videoField.variants.particleCanopy.description',
  },
  3: {
    id: '08b',
    nomeKey: 'videoField.variants.waterMonolith.name',
    descricaoKey: 'videoField.variants.waterMonolith.description',
  },
} as const;

export type Variante = keyof typeof VARIANTES;

export const VARIANTE_PADRAO: Variante = 1;

/** Converte o pedaço final da URL em variante. Qualquer coisa fora cai na padrão. */
export function varianteDaRota(pathname: string): Variante {
  const ultimo = pathname.split('/').filter(Boolean).pop();
  const n = Number(ultimo);
  return n === 2 || n === 3 ? n : VARIANTE_PADRAO;
}

export function XtreedVideoField({
  variante = VARIANTE_PADRAO,
  className,
}: {
  variante?: Variante;
  className?: string;
}) {
  const { id } = VARIANTES[variante];
  const video = useRef<HTMLVideoElement>(null);
  const [pronto, setPronto] = useState(false);

  // Preferência de movimento reduzido: fica só o pôster, sem baixar o vídeo.
  const [semMovimento, setSemMovimento] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const ouvir = (e: MediaQueryListEvent) => setSemMovimento(e.matches);
    mq.addEventListener('change', ouvir);
    return () => mq.removeEventListener('change', ouvir);
  }, []);

  // Trocar de variante troca o arquivo: sem o load() o <video> segue no antigo.
  useEffect(() => {
    setPronto(false);
    video.current?.load();
  }, [id]);

  const poster = `/brand/xtreed-${id}.jpg`;

  return (
    <div
      className={`${className ?? ''} overflow-hidden bg-background`}
      aria-hidden="true"
    >
      {/*
       * O pôster fica por baixo em <img>, não como atributo poster= do vídeo:
       * assim ele aparece igual quando o vídeo ainda não carregou, quando
       * falha, e quando o movimento está desativado — um caminho só.
       */}
      <img
        src={poster}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
      />

      {!semMovimento && (
        <video
          ref={video}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onCanPlay={() => setPronto(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            pronto ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <source src={`/brand/xtreed-${id}.mp4`} type="video/mp4" />
        </video>
      )}

      {/* Sangria para o fundo do formulário, para o painel não cortar seco. */}
      <div className="absolute inset-y-0 left-0 w-32 bg-linear-to-r from-background to-transparent" />
    </div>
  );
}
